/* Copyright 2025 Supovia LLC */

'use client'

import { hydrate, QueryClientProvider } from '@tanstack/react-query'
import createQueryClient from './createQueryClient.js'

// The browser's cache: one per document, so it is right to build it at module
// scope. A server render must NOT use it — the module is evaluated once per
// Lambda container and would then be shared by every request that container
// serves — so `supovia/whitelabel`'s `ssr.jsx` passes its own per-request client
// through `client`.
const browserQueryClient = createQueryClient()

// Adopt the cache the server already filled, before anything renders.
//
// Without this the browser starts empty, so the very first render suspends on
// the website the server had already fetched and rendered: the fully formed page
// is replaced by the Suspense fallback, then re-appears once the same request
// has been made a second time. That flash is the "double render" — the client
// re-fetching what is already on the page.
//
// Only ever runs in the browser, and only when a server render put a state
// there, so a page rendered without it (or a test importing this module) is
// unaffected.
if (typeof window !== 'undefined' && window.__REACT_QUERY_STATE__) {
  hydrate(browserQueryClient, window.__REACT_QUERY_STATE__)
}

const SupoviaProvider = ({ children, client = browserQueryClient }) => (
  <QueryClientProvider client={client}>{children}</QueryClientProvider>
)

export default SupoviaProvider
